/**
 * OKLCH ⇔ HEX 変換と補色ペア生成ユーティリティ。
 *
 * 知覚均等色空間である OKLCH 上で補色（色相 +180 度）を計算することで、
 * RGB / HSL ベースの素朴な補色計算で生じる「明度の知覚ズレ」を避ける。
 * そのため本モジュールでは `culori` の OKLCH 実装を採用する。
 *
 * 設計参照:
 * - 設計書 §5.6「カラーシステム / 補色ペア生成ロジック」
 * - Phase B（色システム拡張）で追加
 */

import { clampChroma, converter, formatHex } from 'culori';

/** sRGB ガマット外などで HEX 化できなかった場合のフォールバック（純黒）。 */
const HEX_FALLBACK = '#000000';

/** 明度ラウンド用の高い方のレンジ（`0.75..0.92`）。 */
const L_HIGH_MIN = 0.75;
const L_HIGH_MAX = 0.92;

/** 明度ラウンド用の低い方のレンジ（`0.18..0.40`）。 */
const L_LOW_MIN = 0.18;
const L_LOW_MAX = 0.4;

/** 彩度レンジ（両色共通、`0.08..0.20`）。控えめな彩度でブランド調を維持。 */
const C_MIN = 0.08;
const C_MAX = 0.2;

/** 補色側色相に与える ±15 度の揺らぎ幅。 */
const HUE_JITTER_DEG = 30;

/** 高レンジ下限と低レンジ上限の中点（0.575）。明度をどちら側へ振るかの境界。 */
const L_MIDPOINT = (L_HIGH_MIN + L_LOW_MAX) / 2;

/** Text（インク）用の低彩度レンジ。面の上に乗る文字なので彩度を強く落とす。 */
const TEXT_C_MIN = 0.01;
const TEXT_C_MAX = 0.06;

/** Text（インク）用の明度レンジ。面との ΔL を十分確保するため、
 * Surface / Inner の通常レンジ（0.18..0.40 / 0.75..0.92）より外側に置く。 */
const TEXT_L_DARK_MIN = 0.12;
const TEXT_L_DARK_MAX = 0.3;
const TEXT_L_LIGHT_MIN = 0.88;
const TEXT_L_LIGHT_MAX = 0.97;

/** Text が面（Surface）の色相を「弱く」受け継ぐ際の揺らぎ幅（±15 度）。 */
const TEXT_HUE_JITTER_DEG = 30;

/** OKLCH → Rgb/Oklch 変換用コンバータ（`culori` 推奨のインスタンス化パターン）。 */
const toOklch = converter('oklch');

/**
 * 与えた範囲 `[min, max)` 内の一様乱数を `rng` から引く小ヘルパ。
 *
 * @param rng 0..1 を返す乱数関数。
 * @param min 下限（含む）。
 * @param max 上限（含まない）。
 * @returns `[min, max)` の float。
 */
function sample(rng: () => number, min: number, max: number): number {
  return min + rng() * (max - min);
}

/**
 * OKLCH 値から sRGB ガマット内に収まる HEX 文字列を生成する内部ヘルパ。
 *
 * OKLCH は P3 以上の広色域を表現できるため、`formatHex` を直接呼ぶと
 * ガマット外で `undefined` が返りうる。`clampChroma` で chroma を詰めて
 * sRGB に収めることで HEX 化の成功率を高める。それでも失敗した場合は
 * `HEX_FALLBACK`（純黒）にフォールバックする。
 *
 * @param l 明度 0..1。
 * @param c 彩度 0..∞（OKLCH では 0.4 程度で十分鮮やか）。
 * @param h 色相 0..360 度。
 * @returns `#rrggbb` 形式の HEX 文字列。
 */
function oklchToHexSafe(l: number, c: number, h: number): string {
  const clamped = clampChroma({ mode: 'oklch', l, c, h }, 'oklch');
  const hex = formatHex(clamped);
  return hex ?? HEX_FALLBACK;
}

/**
 * HEX カラー文字列（`#ff3300` 等）を OKLCH に変換する。
 *
 * 彩度 0 のグレー色では OKLCH の色相が定義できないため `culori` は
 * `h` を `undefined` で返す。本関数は下流の扱いを単純化するため
 * その場合 `h = 0` を返す（spec §5.6 で色相を引き継ぐ必要はない前提）。
 *
 * @param hex `#rgb` / `#rrggbb` / `#rrggbbaa` 等、`culori` が解釈できる HEX 表記。
 * @returns `{ l: 0..1, c: 0..~0.4, h: 0..360 }` 形式の OKLCH 値。
 */
export function hexToOklch(hex: string): { l: number; c: number; h: number } {
  const oklch = toOklch(hex);
  if (oklch === undefined) {
    // `culori` は未知の文字列に対して `undefined` を返す。呼び出し側のバグなので明示的に失敗させる。
    throw new Error(`hexToOklch: invalid color string: ${hex}`);
  }
  return {
    l: oklch.l,
    c: oklch.c,
    h: oklch.h ?? 0,
  };
}

/**
 * OKLCH 値から HEX カラー文字列を生成する。
 *
 * sRGB ガマット外の OKLCH は `clampChroma` により彩度を詰めて sRGB に射影する。
 * それでも HEX 化できない異常系では `#000000` にフォールバックする。
 *
 * @param l 明度 0..1。
 * @param c 彩度（OKLCH）。負値や過大値はクランプされる。
 * @param h 色相（度、0..360）。範囲外でも `culori` 側で正規化される。
 * @returns `#rrggbb` 形式の HEX 文字列。
 */
export function oklchToHex(l: number, c: number, h: number): string {
  return oklchToHexSafe(l, c, h);
}

/**
 * 補色ペア（Surface / Inner の 2 色）をランダム生成する。spec §5.6 準拠。
 *
 * アルゴリズム:
 * 1. 色相 H1 を 0..360 から一様ランダム選択、補色 H2 = H1 + 180 ± 15 度のジッタ
 * 2. 明度は一方を高（0.75..0.92）、もう一方を低（0.18..0.40）からランダム。
 *    どちらが Surface / Inner かは 50/50 でランダム決定
 * 3. 彩度は両色とも 0.08..0.20 から独立にランダム
 *
 * Text 色は含めない（ユーザ指示どおり）。
 *
 * @param rng 0..1 の乱数関数。省略時は `Math.random`。
 *            渡せば決定論的に動作するため、シード再現テスト・エクスポート再現に利用可。
 * @returns `{ surface, inner }` の HEX ペア（`#rrggbb`）。
 */
export function randomComplementaryPair(rng: () => number = Math.random): {
  surface: string;
  inner: string;
} {
  // 1. 色相ペア
  const h1 = rng() * 360;
  const hueJitter = (rng() - 0.5) * HUE_JITTER_DEG;
  const h2 = h1 + 180 + hueJitter;

  // 2. 明度ペア（高/低）と Surface/Inner の割り当て
  const lHigh = sample(rng, L_HIGH_MIN, L_HIGH_MAX);
  const lLow = sample(rng, L_LOW_MIN, L_LOW_MAX);
  const surfaceIsHigh = rng() < 0.5;

  // 3. 彩度（両色独立）
  const cA = sample(rng, C_MIN, C_MAX);
  const cB = sample(rng, C_MIN, C_MAX);

  // 明度を Surface / Inner に割り当てたうえで、色相ペア (h1, h2) を対応づける。
  // どちら側に h1 を振っても補色関係は保たれるため、シンプルに surface = h1 側で固定する。
  const surfaceL = surfaceIsHigh ? lHigh : lLow;
  const innerL = surfaceIsHigh ? lLow : lHigh;

  return {
    surface: oklchToHexSafe(surfaceL, cA, h1),
    inner: oklchToHexSafe(innerL, cB, h2),
  };
}

/**
 * 固定色（ロック済み）に対する補色を 1 色だけランダム生成する。
 *
 * カラーロック機能（AppState.colorLocks）で片方の色が固定されているとき、
 * ランダマイズはこちらのモードを使う: 固定色を `hexToOklch` で OKLCH に
 * 変換し、その色相 +180 ± 15 度のジッタで補色相を決める。
 *
 * 明度は固定色と**反対側**のレンジから引く（固定色が明るければ 0.18..0.40、
 * 暗ければ 0.75..0.92）。`randomComplementaryPair` の高/低ペア構造を
 * 片側固定でも保ち、Surface / Inner 間のコントラストが潰れないようにする。
 * 彩度は既存と同じ 0.08..0.20。
 *
 * @param lockedHex 固定側の HEX 文字列（`#rrggbb` 等）。
 * @param rng 0..1 の乱数関数。省略時は `Math.random`。
 * @returns 補色側の HEX 文字列（`#rrggbb`）。
 */
export function randomComplementaryTo(lockedHex: string, rng: () => number = Math.random): string {
  const locked = hexToOklch(lockedHex);

  const hueJitter = (rng() - 0.5) * HUE_JITTER_DEG;
  const h = locked.h + 180 + hueJitter;

  // 中点（L_MIDPOINT）を境に反対側へ振る。
  const l =
    locked.l >= L_MIDPOINT
      ? sample(rng, L_LOW_MIN, L_LOW_MAX)
      : sample(rng, L_HIGH_MIN, L_HIGH_MAX);

  const c = sample(rng, C_MIN, C_MAX);

  return oklchToHexSafe(l, c, h);
}

/** OKLCH の内部表現（`randomHarmoniousTrio` の導出計算用）。 */
interface Oklch {
  l: number;
  c: number;
  h: number;
}

/**
 * Text 色相モード（トリオ生成時の Text の導出方針）。
 *
 * Surface / Inner の生成は全モード共通（補色ペア）で、Text のみ分岐する:
 * - `'triad'` — Surface の hue +120 度付近に三つ目の色相を立てる。chroma は
 *   通常レンジ（0.08..0.20）を残しつつ、面に対する ΔL を強めに確保。
 * - `'echo'` — Inner と同じ hue / chroma を引き継ぎ、L だけ面と反対側へ
 *   ずらす。
 * - `'ink'` — chroma を大きく落とし（0.01..0.06）、面の hue を弱く受け継ぐ
 *   墨寄り。L でコントラストを立てる。
 */
export type TextHarmonyMode = 'triad' | 'echo' | 'ink';

/** triad モードで Surface hue に足す三色配色オフセット（度）。 */
const TRIAD_HUE_OFFSET_DEG = 120;

/**
 * `randomHarmoniousTrio` のアンカー（ロック済み色）。
 *
 * 指定されたフィールドは HEX のまま結果へ素通しされ（再エンコードによる
 * 丸め劣化を避ける）、OKLCH に持ち上げた値が残りの色の導出基準になる。
 */
export interface TrioAnchors {
  /** Surface のロック色（HEX）。 */
  surface?: string;
  /** Inner のロック色（HEX）。 */
  inner?: string;
  /** Text のロック色（HEX）。 */
  text?: string;
}

/** `randomHarmoniousTrio` の結果（すべて `#rrggbb`）。 */
export interface ColorTrio {
  surface: string;
  inner: string;
  text: string;
}

/**
 * Surface / Inner / Text の調和トリオを OKLCH 空間で生成する。
 *
 * 各色の役割:
 * - **Surface** — 明るい L（0.75..0.92）。トリオの「面」。
 * - **Inner** — Surface の補色（hue +180 ± 15 度）で、L は Surface の
 *   反対側レンジ。二相モデルのコントラストを担保する。
 * - **Text** — 導出は `mode`（`TextHarmonyMode`）で分岐する。いずれの
 *   モードでも L は面（Surface）と ΔL が十分開く側から取る:
 *   triad = hue +120 の三色配色 / echo = Inner の hue・chroma 継承 /
 *   ink = 低彩度の墨（面の hue を弱く受け継ぐ）。
 *
 * アンカー（ロック済み色）がある場合はそれを `hexToOklch` で OKLCH に
 * 持ち上げて導出基準にする。基準の優先順は Surface → Inner → Text:
 * Surface が自由で Inner がロックなら Surface を Inner の補色として逆導出、
 * Text だけロックなら現行モードの導出式を逆算して面の hue を推定する。
 *
 * Background は本関数の対象外（ランダマイズ対象は 3 色のみ）。
 * HEX への最終変換は既存の `oklchToHexSafe` に集約（境界の維持）。
 *
 * @param anchors ロック済み色。省略時は全色フリー（トリオ一括生成）。
 * @param mode Text の色相モード。省略時は `'ink'`（従来挙動）。
 * @param rng 0..1 の乱数関数。省略時は `Math.random`。
 * @returns `{ surface, inner, text }` の HEX トリオ。アンカー指定色は
 *          入力 HEX がそのまま返る。
 */
export function randomHarmoniousTrio(
  anchors: TrioAnchors = {},
  mode: TextHarmonyMode = 'ink',
  rng: () => number = Math.random,
): ColorTrio {
  /** アンカーの反対側レンジから L を引く（ペアのコントラスト維持）。 */
  const oppositeL = (anchorL: number): number =>
    anchorL >= L_MIDPOINT ? sample(rng, L_LOW_MIN, L_LOW_MAX) : sample(rng, L_HIGH_MIN, L_HIGH_MAX);

  /** 面に対して ΔL が強めに開く側の L（Text 用の外側レンジから引く）。 */
  const contrastTextL = (faceL: number): number =>
    faceL >= L_MIDPOINT
      ? sample(rng, TEXT_L_DARK_MIN, TEXT_L_DARK_MAX)
      : sample(rng, TEXT_L_LIGHT_MIN, TEXT_L_LIGHT_MAX);

  // --- Surface を確定（役割: 明るい L。アンカー優先順に導出基準を選ぶ）---
  let surface: Oklch;
  if (anchors.surface !== undefined) {
    surface = hexToOklch(anchors.surface);
  } else if (anchors.inner !== undefined) {
    // Inner がアンカー → Surface はその補色・反対側 L。
    const inner = hexToOklch(anchors.inner);
    surface = {
      h: inner.h + 180 + (rng() - 0.5) * HUE_JITTER_DEG,
      l: oppositeL(inner.l),
      c: sample(rng, C_MIN, C_MAX),
    };
  } else if (anchors.text !== undefined) {
    // Text がアンカー → 現行モードの Text 導出式を逆算して面の hue を推定。
    // triad: text = surface + 120 / echo: text = inner = surface + 180 /
    // ink: text ≈ surface。L はいずれも「テキストと ΔL が開く側」の逆。
    const text = hexToOklch(anchors.text);
    const hueBack = mode === 'triad' ? -TRIAD_HUE_OFFSET_DEG : mode === 'echo' ? -180 : 0;
    surface = {
      h: text.h + hueBack + (rng() - 0.5) * TEXT_HUE_JITTER_DEG,
      l: oppositeL(text.l),
      c: sample(rng, C_MIN, C_MAX),
    };
  } else {
    surface = {
      h: rng() * 360,
      l: sample(rng, L_HIGH_MIN, L_HIGH_MAX),
      c: sample(rng, C_MIN, C_MAX),
    };
  }

  // --- Inner: Surface の補色、反対側 L（全モード共通） ---
  const inner: Oklch =
    anchors.inner !== undefined
      ? hexToOklch(anchors.inner)
      : {
          h: surface.h + 180 + (rng() - 0.5) * HUE_JITTER_DEG,
          l: oppositeL(surface.l),
          c: sample(rng, C_MIN, C_MAX),
        };

  // --- Text: モード別導出（L は常に面との ΔL を強めに確保） ---
  let text: Oklch;
  if (anchors.text !== undefined) {
    text = hexToOklch(anchors.text);
  } else if (mode === 'triad') {
    // 三色配色: Surface hue +120 付近。chroma は通常レンジを残す。
    text = {
      h: surface.h + TRIAD_HUE_OFFSET_DEG + (rng() - 0.5) * HUE_JITTER_DEG,
      l: contrastTextL(surface.l),
      c: sample(rng, C_MIN, C_MAX),
    };
  } else if (mode === 'echo') {
    // Inner の hue / chroma を引き継ぎ、L だけ面と反対側へずらす。
    text = {
      h: inner.h,
      l: contrastTextL(surface.l),
      c: inner.c,
    };
  } else {
    // ink: 低彩度の墨。面の hue を弱く受け継ぐ。
    text = {
      h: surface.h + (rng() - 0.5) * TEXT_HUE_JITTER_DEG,
      l: contrastTextL(surface.l),
      c: sample(rng, TEXT_C_MIN, TEXT_C_MAX),
    };
  }

  // アンカー色は再エンコードせず入力 HEX を素通しする。
  return {
    surface: anchors.surface ?? oklchToHexSafe(surface.l, surface.c, surface.h),
    inner: anchors.inner ?? oklchToHexSafe(inner.l, inner.c, inner.h),
    text: anchors.text ?? oklchToHexSafe(text.l, text.c, text.h),
  };
}
